import React from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { X } from 'lucide-react';
import type {
  DemoTeamMember,
  DemoTeamRole,
} from '../../services/api/types.gen';

/**
 * A list of people to invite, each with one sandbox role.
 *
 * Drawn twice: Roger's own team, and the agency's people. They are stored apart
 * because they are chosen differently -- the Roger team is the same handful
 * every time and has a saved default, and the agency's people are different for
 * every sandbox and must never reach that default -- but they are invited the
 * same way and get the same roles, so they are edited by the same rows.
 *
 * Laid out like the visits table in the Demo content tab: a column header, small
 * controls on a grid, and one plain X per row.
 */

/** Email takes the room; role and the remove button take what they need.
 *
 * 9rem is what "Field clinician" needs beside the picker's chevron, and no
 * more. The list sits in a column half the box wide now, so every rem the role
 * does not use is a rem of the address you can actually read. */
const COLUMNS = 'grid-cols-[minmax(0,1fr)_9rem_1.5rem]';

export const DemoTeamRows: React.FC<{
  members: DemoTeamMember[];
  roles: { value: DemoTeamRole; label: string }[];
  /** Distinguishes the two lists for a screen reader, and for the placeholder. */
  label: string;
  placeholder: string;
  onChange: (members: DemoTeamMember[]) => void;
}> = ({ members, roles, label, placeholder, onChange }) => {
  const update = (index: number, patch: Partial<DemoTeamMember>) =>
    onChange(
      members.map((member, i) =>
        i === index ? { ...member, ...patch } : member
      )
    );

  return (
    <div>
      <div
        aria-hidden='true'
        className={`hidden gap-2 px-1 pb-1 text-xs font-medium text-muted-foreground md:grid ${COLUMNS}`}
      >
        <span>Email</span>
        <span>Role</span>
        <span />
      </div>
      <ul>
        {members.map((member, index) => (
          <li
            key={index}
            className={`grid items-center gap-2 px-1 py-1 ${COLUMNS}`}
          >
            <Input
              inputSize='sm'
              aria-label={`${label} email ${index + 1}`}
              value={member.email}
              onChange={event => update(index, { email: event.target.value })}
              placeholder={placeholder}
            />
            <Select
              value={member.role}
              onValueChange={value =>
                update(index, { role: value as DemoTeamRole })
              }
            >
              <SelectTrigger
                selectSize='sm'
                aria-label={`${label} role ${index + 1}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type='button'
              aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
              onClick={() => onChange(members.filter((_, i) => i !== index))}
              className='inline-flex justify-self-end rounded text-muted-foreground hover:text-foreground'
            >
              <X className='h-4 w-4' />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DemoTeamRows;
